import { Component, ChangeDetectionStrategy, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
    MenuBadge,
    MenuMode,
    MenuService,
    MenuStore,
    MyItemsServerStateEnum,
    MyItemsService,
    MyItemsStore,
    RequestStatusFetcherService,
    HeaderActionsService,
    NavAction,
    HeaderAction,
    MenuItem
} from '@frontend-site/main-header/domain';
import { ProjectStore, SessionRepository, SystemService, SystemStore } from '@frontend-site/shared/core';
import { Observable, tap, switchMap, takeUntil, Subject, of, distinctUntilKeyChanged } from 'rxjs';
import { HybridCommunicationService, HybridMessagePayloadService } from '@frontend-site/shared/hybrid';
import { CopyDetailsService } from '@frontend-site/main-header/domain';
import { RouterState, RouterStoreService } from '@frontend-site/shared/router-store';
import { PDialogService } from '@frontend-site/ui/components';
import { RequestStatusDialogComponent } from './request-status/dialogs/request-status-dialog/request-status-dialog.component';
import { UserMenuAction } from '@frontend-site/main-header/ui';
import { AuthenticationService } from '@frontend-site/auth/domain';
import { UserService } from '@frontend-site/users/domain';
import { preventOpenSearch$ } from './main-header-actions/main-header-open-search.service';
import { ProjectType } from '@frontend-site/shared/data';
import { FocusGrabWorkaroundService } from './focus-grab-workaround.service';
import { AiModeService, AISensitive, AIVersionMode } from '@panaya/ai-chat/features';

const RECENT_ITEMS_COUNT = 5;

@Component({
    selector: 'p-main-header',
    templateUrl: './main-header.component.html',
    styleUrls: ['./main-header.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [CopyDetailsService]
})
export class MainHeaderComponent implements OnInit, OnDestroy {
    protected readonly preventOpenSearch$ = preventOpenSearch$;
    isFreeze$ = this.hybridMessagePayloadService.freezeMainHeader$;
    destroy$ = new Subject<void>();
    menuMode: typeof MenuMode = MenuMode;
    menu$ = this.menuService.mainHeaderMenu$;
    myItems$ = this.myItemsStore.myItems$;
    projectId$ = this.projectStore.projectId$;
    routeState$: Observable<RouterState>;
    currentPath = '';
    mode: NavAction | null = null;
    displayMegaMenu = false;
    badgeArray: { [key: string]: MenuBadge } = {
        'PROJECT.EXECUTION': {
            value: MyItemsServerStateEnum.MY_TESTS,
            tooltip: 'Layouts.Masthead.MY_TESTS_NOTIFICATION'
        },
        'PROJECT.CORRECTIONS': {
            value: MyItemsServerStateEnum.MY_CORRECTIONS,
            tooltip: 'Layouts.Masthead.MY_CORRECTIONS_NOTIFICATION'
        },
        'PROJECT.DEFECTS': {
            value: MyItemsServerStateEnum.MY_DEFECTS,
            tooltip: 'Layouts.Masthead.MY_DEFECTS_NOTIFICATION'
        },
        'PROJECT.DEV_ENTITY.DEVELOPMENT': {
            value: MyItemsServerStateEnum.MY_DEV_ISSUES,
            tooltip: 'Layouts.Masthead.MY_DEV_MODULE_NOTIFICATION'
        }
    };
    actions: HeaderAction[] = [];
    actions$: Observable<HeaderAction[] | null>;
    isExecuteIaUser = false;
    isIcr3Project = true;
    isChatFeatureEnable = false;

    constructor(
        routerStoreService: RouterStoreService,
        private menuService: MenuService,
        readonly session: SessionRepository,
        private readonly copyDetailsService: CopyDetailsService,
        private readonly projectStore: ProjectStore,
        private readonly aiSrv: AiModeService,
        private readonly sysStore: SystemStore,
        private readonly menuStore: MenuStore,
        private readonly router: Router,
        private readonly hybridSvc: HybridCommunicationService,
        private readonly hybridMessagePayloadService: HybridMessagePayloadService,
        private readonly myItemsService: MyItemsService,
        private readonly myItemsStore: MyItemsStore,
        private userService: UserService,
        private readonly requestStatusFetcherService: RequestStatusFetcherService,
        private headerActionsService: HeaderActionsService,
        private dialog: PDialogService,
        private hybridCommunicationService: HybridCommunicationService,
        private authentication: AuthenticationService,
        private readonly systemService: SystemService,
        private readonly focusGrabWorkaroundService: FocusGrabWorkaroundService
    ) {
        this.routeState$ = routerStoreService.state$;
        routerStoreService.state$
            .pipe(switchMap((state) => menuService.loadMainMenu$({ data: state.data, params: { ...state.params, ...state.queryParams } })))
            .subscribe();

        this.isExecuteIaUser = this.userService.isExecuteIaUser();
        this.actions$ = this.menu$.pipe(
            distinctUntilKeyChanged('state'),
            switchMap((menu) => (menu ? this.headerActionsService.getActions(menu.mode) : of(null))),
            takeUntil(this.destroy$)
        );

        projectStore.projectDetails$
            .pipe(
                tap((projectDetails) => {
                    if (projectDetails?.projectId) {
                        this.myItemsService.stopMyItemsPolling();
                        this.myItemsService.startMyItemsPolling(projectDetails.projectId, projectDetails?.systemId).subscribe();
                    } else {
                        this.myItemsService.stopMyItemsPolling();
                    }
                    if (projectDetails?.projectType) this.isIcr3Project = projectDetails?.projectType === ProjectType.ICR_3;
                }),
                takeUntil(this.destroy$)
            )
            .subscribe();

        this.sysStore.systemDetails$
            .pipe(
                tap((systemDetails) => {
                    if (systemDetails) {
                        const aiCustomerAccountState = {
                            mode: systemDetails.aiVersionMode,
                            enablement: systemDetails.aiEnablement
                        };
                        const aiChatFeatureState = {
                            mode: AIVersionMode.GA,
                            enablement: AISensitive.NotSensitiveData
                        };
                        this.isChatFeatureEnable = this.aiSrv.isFeatureEnabled(
                            aiChatFeatureState,
                            aiCustomerAccountState,
                            systemDetails.aiConsent
                        );
                    }
                }),
                takeUntil(this.destroy$)
            )
            .subscribe();
    }

    ngOnInit(): void {
        this.systemService.loadSystemsDetails(RECENT_ITEMS_COUNT);
    }

    iconClicked(mode: NavAction): void {
        switch (mode) {
            case NavAction.Settings:
                this.currentPath = this.router.url;
                break;
            case NavAction.CopyDetails:
                this.handleCopyDetails();
                break;
            case NavAction.RequestStatus:
                if (this.projectStore.projectId) {
                    this.openRequestStatusDialog(this.projectStore.projectId);
                }
                break;
        }
    }

    itemClicked(menuItem: MenuItem): void {
        if (this.isForesightNewAnalysis(menuItem.routePath)) {
            this.hybridCommunicationService.sendSfClearAnalysis();
        }
    }

    private isForesightNewAnalysis(path: string): boolean {
        return path.includes('/discovery');
    }

    toggleDisplay(): void {
        this.displayMegaMenu = !this.displayMegaMenu;
    }

    focusout(event: FocusEvent): void {
        if ((<HTMLTextAreaElement>event?.relatedTarget)?.className.includes('item-text')) return;
        this.displayMegaMenu = false;
    }

    gotoMyItems(menuBadge: MenuBadge): void {
        if (this.projectStore.projectId) {
            this.myItemsService.goToRelevantView(menuBadge, this.projectStore.projectId);
        }
    }

    handleCopyDetails(): void {
        this.copyDetailsService.handleCopyDetails().subscribe();
    }

    handleUserMenuAction(action: UserMenuAction): void {
        switch (action) {
            case 'USER_PROFILE':
                break;
            case 'SWITCH_ACCOUNT':
                break;
            case 'LOGOUT':
                this.authentication.logout();
                break;
            default:
                break;
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
    }

    private openRequestStatusDialog(projectId: number): void {
        this.requestStatusFetcherService.getRequestStatus(projectId).subscribe((rows) => {
            const config = {
                panelClass: rows?.length ? 'p-dialog-large' : 'p-dialog-small',
                data: { projectId, rows }
            };
            this.dialog.open(RequestStatusDialogComponent, config);
        });
    }

    public menuId(index: number, menu: MenuItem): string | undefined {
        return menu.id;
    }

    public onMouseEnter(event: MouseEvent): void {
        this.focusGrabWorkaroundService.setHtmlToFocus(event);
    }
}
